import { Injectable } from '@angular/core';
import { BehaviorSubject, tap } from 'rxjs';
import { CoreItemGate } from '../../../../../backend/src/core/core-item/core-item.gate';
import { WorkspaceIdRequest } from '../../../../../backend/src/core/request-response';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { LambdaService } from '../../shared/core/lambda.service';
import { Id } from '../../shared/id/id';
import { CoreItemType, VertexRelation, VertexRelationType } from '../core-item/core-item';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';

@Injectable({
  providedIn: 'root',
})
export class RelationService {
  private _idAndItem = new Map<string, VertexRelation>();

  private _changeSubject = new BehaviorSubject<VertexRelation | VertexRelation[] | undefined | number>(undefined);

  constructor(
    private workspaceHolderService: WorkspaceHolderService,
    private lambdaService: LambdaService
  ) {}

  refreshAllRelationList$() {
    const request: WorkspaceIdRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
    };

    return this.lambdaService.run$(ResourceName.coreItem, CoreItemGate.actions.getAllRelationList, request).pipe(
      tap((relationList: VertexRelation[]) => {
        relationList.forEach((c) => {
          this._idAndItem.set(this.getKey(c), c);
        });
      })
    );
  }

  setRelationInCache(relation: VertexRelation) {
    this._idAndItem.set(this.getKey(relation), relation);
  }

  change() {
    return this._changeSubject.next(1);
  }

  getRelationListByFromId(fromId: string, config?: { relationType?: VertexRelationType; toType?: CoreItemType }) {
    const allRelationList = this._idAndItem.values();

    const result: VertexRelation[] = [];
    for (const relation of allRelationList) {
      if (relation.from === fromId) {
        if (config?.relationType && config?.relationType !== relation.relation) {
          continue;
        }

        const toId = relation.to;
        const to = Id.backend.chop(toId);

        if (config?.toType) {
          if (to?.coreType === config?.toType) {
            result.push(relation);
          }
        } else {
          result.push(relation);
        }
      }
    }
    return result;
  }

  getRelationListByToId(
    toId: string,
    config?: {
      relationType?: VertexRelationType;
      fromType?: CoreItemType;
    }
  ) {
    const allRelationList = this._idAndItem.values();

    const result: VertexRelation[] = [];
    for (const relation of allRelationList) {
      if (relation.to === toId) {
        if (config?.relationType && config?.relationType !== relation.relation) {
          continue;
        }

        const fromId = relation.from;
        const from = Id.backend.chop(fromId);

        if (config?.fromType) {
          if (from?.coreType === config?.fromType) {
            result.push(relation);
          }
        } else {
          result.push(relation);
        }
        result.push(relation);
      }
    }
    return result;
  }

  addRelationListInCache(relationList?: VertexRelation[]) {
    if (!relationList) {
      return;
    }
    relationList?.forEach((c) => {
      this._idAndItem.set(this.getKey(c), c);
    });
    this._changeSubject.next(relationList);
  }

  deleteRelationListInCache(relationList?: VertexRelation[]) {
    if (!relationList) {
      return;
    }
    relationList?.forEach((c) => {
      this._idAndItem.delete(this.getKey(c));
    });
    this._changeSubject.next(relationList);
  }

  private getKey(relation: VertexRelation) {
    return `${relation.from}_${relation.to}_${relation.relation}`;
  }
}
